import {useState,useEffect,useRef,useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {getRepository} from '../data/repository';
import type {SearchResult} from '../types/vault';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiSearch,FiX,FiUser,FiCalendar,FiFileText,FiList,FiCommand,FiLock,FiChevronRight,FiGitBranch,FiAlertCircle,FiRefreshCw}=FiIcons;

interface SmartSearchProps {
  isOpen: boolean;
  onClose: ()=> void;
  onOpenDocument: (id: string)=> void;
  onOpenUpload: ()=> void;
}

export function SmartSearch({isOpen,onClose,onOpenDocument,onOpenUpload}: SmartSearchProps) {
  const [query,setQuery]=useState('');
  const [results,setResults]=useState<SearchResult[]>([]);
  const [loading,setLoading]=useState(false);
  const [selectedIndex,setSelectedIndex]=useState(0);
  const [error,setError]=useState<string | null>(null);
  const inputRef=useRef<HTMLInputElement>(null);
  const resultsRef=useRef<HTMLDivElement>(null);
  const navigate=useNavigate();
  const searchTimeoutRef=useRef<ReturnType<typeof setTimeout>>();
  const lastQueryRef=useRef<string>('');

  useEffect(()=> {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedIndex(0);
      setError(null);
      setTimeout(()=> inputRef.current?.focus(),50);
    }
  },[isOpen]);

  const performSearch=useCallback(async (q: string)=> {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res=await getRepository().searchVault(q);
      if (lastQueryRef.current===q) {
        setResults(res);
        setSelectedIndex(0);
      }
    } catch (err) {
      if (lastQueryRef.current===q) {
        setError(err instanceof Error ? err.message : 'Search failed. Please try again.');
      }
    } finally {
      if (lastQueryRef.current===q) {
        setLoading(false);
      }
    }
  },[]);

  useEffect(()=> {
    if (!isOpen) return;
    lastQueryRef.current=query;
    clearTimeout(searchTimeoutRef.current);
    if (query.trim().length===0) {
      setResults([]);
      setLoading(false);
      return;
    }
    searchTimeoutRef.current=setTimeout(()=> {
      performSearch(query);
    },150);
    return ()=> clearTimeout(searchTimeoutRef.current);
  },[query,isOpen,performSearch]);

  const handleSelect=(result: SearchResult)=> {
    onClose();
    if (result.resultType==='command') {
      if (result.route==='upload') {
        onOpenUpload();
      } else if (result.route) {
        navigate(result.route);
      }
    } else if ((result.resultType==='document' || result.resultType==='document_version') && result.documentId) {
      onOpenDocument(result.documentId);
    } else if (result.route) {
      navigate(result.route);
    }
  };

  useEffect(()=> {
    if (!isOpen) return;
    const handleKeyDown=(e: KeyboardEvent)=> {
      if (e.key==='Escape') {
        onClose();
      } else if (e.key==='ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev=> (prev < results.length - 1 ? prev + 1 : prev));
      } else if (e.key==='ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev=> (prev > 0 ? prev - 1 : prev));
      } else if (e.key==='Enter' && results.length > 0) {
        e.preventDefault();
        handleSelect(results[selectedIndex]);
      }
    };
    document.addEventListener('keydown',handleKeyDown);
    return ()=> document.removeEventListener('keydown',handleKeyDown);
  },[isOpen,results,selectedIndex,onClose]);

  useEffect(()=> {
    if (resultsRef.current && results.length > 0) {
      const selectedEl=resultsRef.current.children[selectedIndex] as HTMLElement;
      if (selectedEl) {
        selectedEl.scrollIntoView({block: 'nearest'});
      }
    }
  },[selectedIndex,results]);

  if (!isOpen) return null;

  const getIcon=(type: string)=> {
    switch (type) {
      case 'profile': return FiUser;
      case 'financial_year': return FiCalendar;
      case 'document': return FiFileText;
      case 'reference_block': return FiList;
      case 'document_version': return FiGitBranch;
      case 'command': return FiCommand;
      default: return FiSearch;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-0 lg:pt-[10vh]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full h-full lg:h-auto lg:max-h-[80vh] lg:max-w-2xl bg-white lg:rounded-2xl shadow-2xl flex flex-col overflow-hidden" role="dialog" aria-label="Search Vault">
        <div className="flex items-center px-4 py-3 border-b border-slate-100 shrink-0">
          <SafeIcon icon={FiSearch} className="w-5 h-5 text-slate-400 mr-3" />
          <input ref={inputRef} type="text" value={query} onChange={e=> setQuery(e.target.value)} placeholder="Search profiles, documents, years, commands..." className="flex-1 bg-transparent border-none focus:outline-none text-base lg:text-lg text-slate-900 placeholder-slate-400" aria-label="Search query" />
          {query && (
            <button onClick={()=> setQuery('')} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 mr-1" aria-label="Clear search">
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="Close search">
            <span className="text-sm font-medium">Cancel</span>
          </button>
          <div className="hidden lg:flex items-center ml-2 space-x-1">
            <kbd className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-xs text-slate-500 font-sans">ESC</kbd>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" ref={resultsRef}>
          {loading && results.length===0 ? (
            <div className="p-8 text-center text-slate-500">
              <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-[#0F766E] rounded-full mx-auto" />
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <p className="text-red-500 text-sm mb-3">{error}</p>
              <button onClick={()=> performSearch(query)} className="inline-flex items-center px-3 py-1.5 text-sm text-[#0F766E] hover:bg-[#0F766E]/10 rounded-lg font-medium">
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 mr-1.5" />Retry
              </button>
            </div>
          ) : query.length > 0 && query.length < 2 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              Type at least 2 characters to search...
            </div>
          ) : results.length===0 && query.length >=2 ? (
            <div className="p-8 text-center text-slate-500 text-sm">
              No results found for "{query}"
            </div>
          ) : results.length > 0 ? (
            <div className="py-2">
              {results.map((res,idx)=> {
                const Icon=getIcon(res.resultType);
                const isSelected=idx===selectedIndex;
                return (
                  <button key={`${res.resultType}-${res.id}`} className={`w-full text-left px-4 py-3 flex items-start gap-4 transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`} onClick={()=> handleSelect(res)} onMouseEnter={()=> setSelectedIndex(idx)}>
                    <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#0F766E]/10 text-[#0F766E]' : 'bg-slate-100 text-slate-500'}`}>
                      {res.sensitive && res.resultType==='document' ? (
                        <SafeIcon icon={FiLock} className="w-4 h-4 text-amber-500" />
                      ) : (
                        <SafeIcon icon={Icon} className="w-4 h-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`text-sm font-medium truncate ${isSelected ? 'text-[#0F766E]' : 'text-slate-900'}`}>{res.title}</h4>
                        {res.sensitive && <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">Sensitive</span>}
                      </div>
                      {res.subtitle && <p className="text-xs text-slate-500 truncate mt-0.5">{res.subtitle}</p>}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">{res.resultType.replace('_',' ')}</span>
                        <span className="text-[10px] text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400">{res.matchReason}</span>
                      </div>
                    </div>
                    <div className="shrink-0 self-center">
                      <SafeIcon icon={FiChevronRight} className={`w-4 h-4 ${isSelected ? 'text-[#0F766E]' : 'text-slate-300'}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-400 flex flex-col items-center">
              <SafeIcon icon={FiSearch} className="w-8 h-8 mb-3 opacity-50" />
              <p className="text-sm">Search for profiles, documents, financial years, or commands.</p>
            </div>
          )}
        </div>
        <div className="hidden lg:flex items-center justify-between px-4 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white border rounded">↑</kbd><kbd className="px-1.5 py-0.5 bg-white border rounded">↓</kbd> to navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-white border rounded">↵</kbd> to select</span>
          </div>
        </div>
      </div>
    </div>
  );
}